import { useRef, useState } from 'react';
import { ApiError } from '@rydres/api-client';
import { Button, InfoTip, Notice } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { useProfile } from '../lib/profile.js';

/** Longest side and JPEG quality to try in turn until the photo fits; the API refuses anything over 750 kB. */
const STEPS: Array<[number, number]> = [
  [1024, 0.85],
  [1024, 0.7],
  [800, 0.7],
  [640, 0.6],
];
const MAX_BYTES = 700_000;

/**
 * Redraws the photo smaller as a JPEG and returns it as base64. Redrawing also drops the
 * camera's metadata, including where the photo was taken.
 */
async function shrink(file: File): Promise<string> {
  const src = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = src;
    await img.decode();
    for (const [side, quality] of STEPS) {
      const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, 'image/jpeg', quality));
      if (blob && blob.size <= MAX_BYTES) {
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let bin = '';
        for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
        return btoa(bin);
      }
    }
    throw new Error('photo stays too large');
  } finally {
    URL.revokeObjectURL(src);
  }
}

/** Shows a page photo with Add/Change/Remove; saves at once, separate from the form's Save button. */
export function PhotoField({ kind, url, label, hint }: { kind: 'driver' | 'vehicle'; url: string | null; label: string; hint: string }) {
  const { reload } = useProfile();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not use that photo. Try another one.');
    } finally {
      setBusy(false);
    }
  };
  const frame = kind === 'vehicle' ? 'h-20 w-28' : 'h-20 w-20';
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <span className="text-xs font-bold uppercase tracking-wide text-muted">{label}</span>
        <InfoTip label={label}>{hint}</InfoTip>
      </div>
      <div className="flex items-center gap-4">
        {url ? (
          <img src={url} alt="" className={`${frame} rounded-2xl object-cover`} />
        ) : (
          <div className={`${frame} flex items-center justify-center rounded-2xl bg-card2 text-xs text-muted`}>No photo</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button small disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Saving…' : url ? 'Change' : 'Add a photo'}</Button>
          {url && !busy && <Button small variant="ghost" onClick={() => void run(() => driverApi.removePhoto(kind))}>Remove</Button>}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void run(async () => driverApi.savePhoto(kind, await shrink(file)));
        }}
      />
      {error && <Notice tone="danger">{error}</Notice>}
    </div>
  );
}
